import { isNative } from '@/shared/api/platform'

import { recordClientEvent } from './clientEvents'
import { reportError as reportToNewRelic } from './newrelic'

/**
 * Punto único para reportar un error (spec 2026-11-02-errores-app-nativa).
 *
 * - **Web:** a New Relic (si el usuario aceptó analíticas; su agente ya captura solo los
 *   errores no controlados).
 * - **App nativa:** New Relic no va en las apps (sus claves solo están en el build web de
 *   Cloudflare), así que además se cuenta en nuestro backend como `CLIENT_ERROR`: nombre y
 *   mensaje, ruta y el principio de la traza. Nada más del usuario.
 *
 * Tope de {@link MAX_NATIVE_ERRORS_PER_SESSION} errores distintos por arranque de la app, y
 * cada mensaje una sola vez: un error que se repite en bucle no inunda la tabla.
 */

export const MAX_NATIVE_ERRORS_PER_SESSION = 5

const reported = new Set<string>()

export function reportError(error: Error, attributes?: Record<string, unknown>): void {
  reportToNewRelic(error, attributes)
  if (!isNative()) return
  const detail = `${error.name}: ${error.message}`.slice(0, 200)
  if (reported.has(detail) || reported.size >= MAX_NATIVE_ERRORS_PER_SESSION) return
  reported.add(detail)
  recordClientEvent({
    type: 'CLIENT_ERROR',
    path: window.location.pathname,
    detail,
    stack: error.stack?.slice(0, 1000),
  })
}

function toError(value: unknown): Error {
  if (value instanceof Error) return value
  if (typeof value === 'object' && value !== null && 'message' in value) {
    return new Error(String((value as { message: unknown }).message))
  }
  return new Error(String(value))
}

/**
 * En la app nativa, también los errores que no pasan por una pantalla de error: excepciones
 * sueltas y promesas rechazadas sin `catch`. En la web no hace falta (lo hace New Relic).
 */
export function installNativeErrorReporting(target: Window = window): void {
  if (!isNative()) return
  target.addEventListener('error', (event) => {
    reportError(event.error instanceof Error ? event.error : new Error(event.message), {
      source: 'window.error',
    })
  })
  target.addEventListener('unhandledrejection', (event) => {
    reportError(toError(event.reason), { source: 'unhandledrejection' })
  })
}

/** Solo para tests: olvida los errores ya reportados. */
export function resetErrorReportingForTests(): void {
  reported.clear()
}
